import { useState } from "react";
import { PageCollectionLayout } from "../../../../components/pageCollection/PageCollectionLayout";
import { Settings } from "../../constants/tabs";
import { tabsLeft } from "../../constants/tabs.shared";
import { useNavigate } from "react-router";
import { SELLAR_CREDIT_STATEMENT } from "../../../../constants/routes";
import { withSearchParams } from "../../../../utils/withSearchParams";
import { SwitchCreditProviderButton } from "../../components/SwitchCreditProviderButton";
import { useFeatureFlagStore } from "~/hooks/useFeatureFlags";
import { useLegalEntityId } from "~/hooks/api/v2/legalEntity/useLegalEntityId";
import { useLegalEntity } from "~/hooks/api/v2/legalEntity/useLegalEntity";
import { useDebounce } from "use-debounce";
import { useCreditProcessorPaymentAllocationEvents } from "~/hooks/api/v2/creditProcessor/useCreditProcessorPaymentAllocationEvents";
import { TableHeader } from "../../../../components/table/TableHeader";
import { createSellarColumnHelper } from "@sellar-io/components";

import { useMemo } from "react";
import { twMerge } from "tailwind-merge";
import dayjs from "dayjs";
import { formatCurrency } from "@sellar-io/common";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import { CreditProcessorPaymentAllocationEventListResponseType } from "@sellar-io/api-v2/types";
import { TableInfinite } from "../../../../components/table/TableInfinite";
import { ProfilePicture, SpinnerLegacy } from "../../../../components";

export const TwoSellarCreditPaymentAllocationHistory = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);
  const scPaymentAllocationHistoryEnabled = useFeatureFlagStore().isActive(
    "SELLAR_CREDIT_PAYMENT_ALLOCATION_HISTORY",
  );
  const legalEntityId = useLegalEntityId();
  const { data: legalEntity } = useLegalEntity(legalEntityId, {
    enabled: !!legalEntityId,
  });

  const companyHasHokodoAccount = !!legalEntity?.hokodoCompanyId;

  const queryParams = {
    search: debouncedSearch,
    sortBy: "eventTimeAt" as const,
    sortDir: "desc" as const,
    limit: 20,
  };

  const paymentAllocationEventsQuery =
    useCreditProcessorPaymentAllocationEvents(queryParams, {
      refetchOnWindowFocus: false,
    });

  return (
    <PageCollectionLayout
      title="Sellar Credit"
      topComponent={
        companyHasHokodoAccount && (
          <SwitchCreditProviderButton
            onSwitchClick={() =>
              navigate(
                withSearchParams(SELLAR_CREDIT_STATEMENT, {
                  provider: "hokodo",
                }),
              )
            }
            currentProvider="two"
          />
        )
      }
      tabsLeft={tabsLeft({
        provider: "two",
        scPaymentAllocationHistoryEnabled,
      })}
      rightComponent={<Settings provider="two" />}
    >
      <div
        className={"container mx-auto mt-6 flex max-w-325 flex-col bg-zinc-100"}
      >
        <TableHeader
          search={search}
          searchPlaceholder="Search by order, reference, supplier"
          setSearch={setSearch}
        />
        <PaymentAllocationHistoryTable query={paymentAllocationEventsQuery} />
      </div>
    </PageCollectionLayout>
  );
};

type ColumnDataType =
  CreditProcessorPaymentAllocationEventListResponseType[number];

export const PaymentAllocationHistoryTable = ({
  query,
}: {
  query: UseInfiniteQueryResult<
    InfiniteData<CreditProcessorPaymentAllocationEventListResponseType>,
    Error
  >;
}) => {
  const { data, fetchNextPage, isFetching } = query;
  const flatData = useMemo(
    () => data?.pages.flatMap((page) => page.map((row) => row)) ?? [],
    [data],
  );

  const columnHelper = createSellarColumnHelper<ColumnDataType>();

  const columns = [
    columnHelper.accessor("id", {
      header: "",
      enableSorting: false,
      cell: () => {
        return <></>; //Empty column to help with UI padding
      },
    }),
    columnHelper.accessor((row) => row.TwoOrder, {
      id: "order",
      header: "Order",
      enableSorting: false,
      cell: (info) => {
        const twoOrder = info.getValue();
        const orderId = twoOrder?.Order.id;
        const supplier = twoOrder?.Order.Supplier;
        const invoiceId = info.row.original.twoInvoiceId;

        if (!orderId || !supplier) {
          return <div>-</div>;
        }

        return (
          <div className="flex items-center gap-2 px-4 pl-0">
            <ProfilePicture
              size="small"
              logoUrl={supplier.Business.logoUrl}
              fallback="supplier-badge"
            />
            <div className="flex w-full flex-col">
              <span className="text-sm font-medium text-zinc-800">
                {supplier.Business.name}
              </span>
              <span className="text-xs text-zinc-500">{`Order #${orderId}`}</span>
              {invoiceId && (
                <span className="text-xs text-zinc-500">{`Invoice #${invoiceId}`}</span>
              )}
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor("eventTimeAt", {
      id: "occurredAt",
      header: "Occurred at",
      enableSorting: false,
      cell: (info) => dayjs(info.getValue()).format("DD/MM/YYYY"),
    }),
    columnHelper.accessor("paymentBookedAt", {
      id: "paymentBookedAt",
      header: "Payment received",
      enableSorting: false,
      cell: (info) => dayjs(info.getValue()).format("DD/MM/YYYY"),
    }),
    columnHelper.accessor((row) => row, {
      id: "reference",
      header: "Reference",
      enableSorting: false,
      cell: (info) => {
        const row = info.getValue();

        return (
          <div className="flex flex-col text-sm text-zinc-800">
            {row.buyerPaymentReference && (
              <span>Buyer paid using payment #{row.buyerPaymentReference}</span>
            )}
            {row.twoMatchedReference && (
              <span>Matched to payment #{row.twoMatchedReference}</span>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor("paymentAmount", {
      id: "paymentAmount",
      header: "Original payment",
      enableSorting: false,
      cell: (info) => formatCurrency(info.getValue()),
    }),
    columnHelper.accessor("allocationAmount", {
      id: "allocationAmount",
      header: "Transaction",
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();
        const formatted = formatCurrency(value);

        return (
          <span>
            Applied{" "}
            <span
              className={twMerge(
                value <= 0 ? "text-red-600" : "text-emerald-600",
              )}
            >
              {formatted}
            </span>{" "}
            to invoice
          </span>
        );
      },
    }),
  ];

  if (query.isLoading && flatData.length === 0) {
    return <SpinnerLegacy />;
  }

  return (
    <div className={twMerge("max-h-[calc(100vh-250px)] border-zinc-300")}>
      <TableInfinite
        columns={columns}
        data={flatData}
        infiniteQuery={{
          data: flatData,
          fetchNextPage,
          isFetching,
          totalCount: data?.pages[0]?.[0]?.count ?? 0,
        }}
        gridClasses="grid-cols-[16px_minmax(200px,_1fr)_140px_140px_1fr_140px_1fr]"
        roundedBottom={false}
        noResultsText="No payment allocation events found"
      />
    </div>
  );
};
